import { useState } from 'react';
import { Alert, Box, Container, Link, Paper } from '@mui/material';
import { Button, Input, Stepper, Typography, Dialog } from '@sm-dashboard/ui-components';
import chabadLogo from '@/assets/chabad-logo1.png';

const DELETION_STEPS = [
  'הזינו את מספר הטלפון המשויך לחשבונכם',
  'לחצו על "בקשת מחיקת חשבון"',
  'אשרו את הבקשה בחלון האישור',
  'נעבד את הבקשה ונשלח אליכם אישור במייל או ב-SMS'
];

const DELETED_DATA = [
  'פרטי פרופיל אישי (שם, טלפון, דוא"ל, כתובת, תמונות)',
  'העדפות משתמש והגדרות אישיות',
  'קישורים אישיים, טפסים ותוכן שנוצר על ידי המשתמש',
  'נתוני CRM אישיים (אנשי קשר, פעילויות, קבוצות)',
  'היסטוריית התחברות ואסימוני אימות'
];

const RETAINED_DATA = [
  'רשומות תרומות ותשלומים — נשמרות לצורכי חשבונאות ודיווח מס, בהתאם לחוק (עד 7 שנים)',
  'נתונים אנונימיים לצורכי סטטיסטיקה ושיפור השירות',
  'רשומות שנדרשות על פי חוק או צו שיפוטי'
];

const AccountDeletion = () => {
  const [phone, setPhone] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!phone.trim()) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    setSubmitted(true);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box component="img" src={chabadLogo} alt="לוגו חב״ד ישראל" sx={{ maxWidth: 200, height: 'auto' }} />
      </Box>

      <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, backgroundColor: 'common.white', borderRadius: '15px' }}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography variant="h1">בקשת מחיקת חשבון</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="subtitle1" color="primary.main">
            דשבורד השלוחים
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="body2" color="text.secondary">
            מפורסם על ידי חב״ד ישראל · Shlichut Center - MLS Ltd
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            borderColor: 'primary.main',
            borderWidth: 2,
            backgroundColor: theme => theme.palette.primary.main + '08'
          }}
        >
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="h3">שלבי בקשת מחיקת החשבון</Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Stepper
              activeStep={DELETION_STEPS.length}
              orientation="vertical"
              steps={DELETION_STEPS.map((step, index) => `${index + 1}. ${step}`)}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              זמן עיבוד הבקשה: עד 30 יום ממועד האישור. לאחר המחיקה, לא ניתן לשחזר את הנתונים.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h3">נתונים שיימחקו</Typography>
        </Box>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          {DELETED_DATA.map(item => (
            <Box component="li" key={item} sx={{ mb: 0.5 }}>
              <Typography variant="body">{item}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h3">נתונים שיישמרו</Typography>
        </Box>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          {RETAINED_DATA.map(item => (
            <Box component="li" key={item} sx={{ mb: 0.5 }}>
              <Typography variant="body">{item}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="body2" color="text.secondary">
            תקופת שמירה נוספת: נתונים פיננסיים וחשבונאיים יישמרו עד 7 שנים, בהתאם לדרישות החוק בישראל. לאחר תום תקופה
            זו, הנתונים יימחקו או יעברו אנונימיזציה.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 3, mt: 4, borderColor: 'error.light' }}>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="h3">שליחת בקשת מחיקה</Typography>
          </Box>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body2" color="text.secondary">
              הזינו את מספר הטלפון שבו השתמשתם להתחברות לאפליקציה
            </Typography>
          </Box>

          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              בקשת מחיקת החשבון התקבלה. נעבד את הבקשה וניצור איתכם קשר בימים הקרובים.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Input
                  label="מספר טלפון"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="050-0000000"
                  type="tel"
                  fullWidth
                />
              </Box>
              <Box sx={{ minWidth: { sm: 180 }, width: { xs: '100%', sm: 'auto' } }}>
                <Button color="error" onClick={handleSubmit} disabled={!phone.trim()} fullWidth>
                  בקשת מחיקת חשבון
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 5, mb: 2 }}>
          <Typography variant="h3">יצירת קשר</Typography>
        </Box>
        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="body">ניתן גם לפנות אלינו ישירות לבקשת מחיקת חשבון:</Typography>
        </Box>
        <Box sx={{ pl: 2 }}>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body">
              <strong>טלפון:</strong> <Link href="tel:+972515415770">+972-51-541-5770</Link>
            </Typography>
          </Box>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body">
              <strong>דוא״ל תמיכה:</strong> <Link href="mailto:yios770@gmail.com">yios770@gmail.com</Link>
            </Typography>
          </Box>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body">
              <strong>דוא״ל מפתח:</strong>{' '}
              <Link href="mailto:chabad@chabadisrael.co.il">chabad@chabadisrael.co.il</Link>
            </Typography>
          </Box>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body">
              <strong>כתובת:</strong> 1 Tzeirei Chabad, Kfar Chabad, 6084000, Israel
            </Typography>
          </Box>
          <Box sx={{ mb: '0.35em' }}>
            <Typography variant="body">
              <strong>טלפון מפתח:</strong> <Link href="tel:+972584641770">+972-58-464-1770</Link>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, mb: '0.35em' }}>
          <Typography variant="body">
            למידע נוסף, ראו את <Link href="/privacy-policy">מדיניות הפרטיות</Link>.
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            עודכן לאחרונה: יום שלישי, 17 ביוני 2026
          </Typography>
        </Box>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        title="אישור מחיקת חשבון"
        cancelAction
        primaryAction={{ label: 'אישור מחיקה', onClick: handleConfirm, color: 'error' }}
      >
        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="body">האם אתם בטוחים שברצונכם לבקש מחיקת החשבון המשויך למספר {phone}?</Typography>
        </Box>
        <Typography variant="body2" color="error.main">
          פעולה זו בלתי הפיכה. כל הנתונים האישיים יימחקו לצמיתות, למעט נתונים שנדרש לשמור על פי חוק.
        </Typography>
      </Dialog>
    </Container>
  );
};

export default AccountDeletion;
